// src/pages/admin/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Grid,
  BottomNavigation,
  BottomNavigationAction,
  SwipeableDrawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Avatar
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, ArrowBack as ArrowBackIcon, Timer as TimerIcon, People as PeopleIcon, Assignment as AssignmentIcon, LiveTv as LiveTvIcon, History as HistoryIcon, Add as AddIcon, Menu as MenuIcon, Close as CloseIcon, Stars as StarsIcon, WorkspacePremium as PremiumIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { collection, getDocs, query, orderBy, doc, updateDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { db, COLLECTIONS } from '../../api/config';
import {
  getAllUsers,
  updateUser,
  deleteUser,
  getActivityLogs,
  getAnalyticsData,
  manageCourses,
  manageAssignments,
  getLiveClasses,
  moderateLiveClass,
} from '../../api/admin';
import { toggleProStatus } from '../../api/users';
import { endDailyRoom } from '../../api/teacher';

const truncateEmail = (email) => {
  const [username, domain] = email.split('@');
  if (!domain) return email;
  return `${username}@...`;
};

// Field labels in English
const fieldLabels = {
  displayName: 'Name',
  email: 'Email',
  password: 'Password',
  role: 'Role',
  roles: {
    student: 'Student',
    teacher: 'Teacher',
    admin: 'Admin',
    parent: 'Parent'
  }
};

const AdminDashboard = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [users, setUsers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [liveClasses, setLiveClasses] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editType, setEditType] = useState(''); // 'user' | 'assignment'
  const [editData, setEditData] = useState({});
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [newUserData, setNewUserData] = useState({
    displayName: '',
    email: '',
    role: 'student',
    password: ''
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Section titles in English
  const sectionTitles = {
    users: 'Users',
    teachers: 'Teachers',
    students: 'Students',
    parents: 'Parents',
    assignments: 'Assignments',
    liveClasses: 'Live Classes',
    activity: 'Activities'
  };

  const emptyStates = {
    teachers: 'No teachers',
    students: 'No students',
    parents: 'No parents',
    assignments: 'No assignments',
    liveClasses: 'No live classes',
    activity: 'No activities'
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch dailyco_rooms directly from Firestore
      const roomsQuery = query(
        collection(db, COLLECTIONS.DAILYCO_ROOMS),
        orderBy('createdAt', 'desc')
      );
      const roomsSnapshot = await getDocs(roomsQuery);
      const roomsData = roomsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));

      const [
        usersData,
        assignmentsData,
        logsData,
        analyticsData
      ] = await Promise.all([
        getAllUsers(),
        manageAssignments('read'),
        getActivityLogs(),
        getAnalyticsData()
      ]);

      setUsers(usersData);
      setAssignments(assignmentsData);
      setLiveClasses(roomsData);
      setActivityLogs(logsData);
      setAnalytics(analyticsData);
    } catch (err) {
      console.error(err);
      setError('Failed to load data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleEditOpen = (type, data) => {
    setEditType(type);
    setEditData(data);
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    try {
      if (editType === 'user') {
        await updateUser(editData.id, editData);
      } else if (editType === 'assignment') {
        await manageAssignments('update', editData);
      }
      setEditDialogOpen(false);
      loadData();
    } catch (err) {
      console.error(err);
      setError('Failed to update.');
    }
  };

  const handleProToggle = async (userId) => {
    try {
      const updatedUser = await toggleProStatus(userId);
      // If the user exists in the system, trigger an auth context update
      if (updatedUser) {
        // Find all instances of this user in our current users list and update them
        setUsers(users.map(user =>
          user.id === userId ? { ...user, ...updatedUser } : user
        ));
      }
    } catch (err) {
      console.error(err);
      setError('Failed to toggle pro status.');
    }
  };

  const handleDelete = async (type, id) => {
    try {
      if (type === 'user') {
        await deleteUser(id);
      } else if (type === 'assignment') {
        await manageAssignments('delete', { id });
      } else if (type === 'liveClass') {
        // End the Daily.co room - this will both delete the room and update Firestore
        await endDailyRoom(id);
      }
      loadData();
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to delete/end class. Please try again.');
    }
  };

  const handleAddUserSave = async () => {
    try {
      await updateUser(null, newUserData); // Pass null as id for creation
      setAddUserDialogOpen(false);
      setNewUserData({
        displayName: '',
        email: '',
        role: 'student',
        password: ''
      });
      loadData();
    } catch (err) {
      console.error(err);
      setError('Failed to create user.');
    }
  };

  const renderUsersTab = () => {
    const groupedUsers = {
      student: users.filter(u => u.role === 'student'),
      parent: users.filter(u => u.role === 'parent'),
      teacher: users.filter(u => u.role === 'teacher')
    };

    const UserCard = ({ user }) => (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: '#fff',
          borderRadius: 2,
          border: '1px solid #e0e0e0',
          '&:active': {
            transform: 'scale(0.98)',
            transition: 'transform 0.1s'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              bgcolor: '#F5A623',
              color: '#fff',
              width: 50,
              height: 50,
              fontSize: '1.2rem'
            }}
          >
            {user.displayName?.charAt(0) || 'U'}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ color: '#1a1a1a', fontWeight: 500 }}>
              {user.displayName}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              {truncateEmail(user.email)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {user.role === 'student' && (
              <IconButton
                onClick={() => handleProToggle(user.id)}
                sx={{
                  color: user.isPro ? '#FFD700' : '#999',
                  backgroundColor: user.isPro ? 'rgba(255,215,0,0.1)' : 'rgba(0,0,0,0.05)',
                  '&:hover': {
                    backgroundColor: user.isPro ? 'rgba(255,215,0,0.2)' : 'rgba(0,0,0,0.1)'
                  },
                  '&:active': { transform: 'scale(0.95)' }
                }}
              >
                <PremiumIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              onClick={() => handleEditOpen('user', user)}
              sx={{
                color: '#F5A623',
                backgroundColor: 'rgba(245,166,35,0.1)',
                '&:active': { transform: 'scale(0.95)' }
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => handleDelete('user', user.id)}
              sx={{
                color: '#ef4444',
                backgroundColor: 'rgba(239,68,68,0.1)',
                '&:active': { transform: 'scale(0.95)' }
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
    </Paper>
  );

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Teachers Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            sx={{
              color: '#F5A623',
              mb: 2,
              fontWeight: 500,
              px: 1
            }}
          >
            {sectionTitles.teachers}
          </Typography>
          {groupedUsers.teacher.map(user => (
            <UserCard key={user.id} user={user} />
          ))}
          {groupedUsers.teacher.length === 0 && (
            <Typography
              sx={{
                textAlign: 'center',
                color: '#666',
                py: 2
              }}
            >
              {emptyStates.teachers}
            </Typography>
          )}
        </Box>

        {/* Students Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            sx={{
              color: '#F5A623',
              mb: 2,
              fontWeight: 500,
              px: 1
            }}
          >
            {sectionTitles.students}
          </Typography>
          {groupedUsers.student.map(user => (
            <UserCard key={user.id} user={user} />
          ))}
          {groupedUsers.student.length === 0 && (
            <Typography
              sx={{
                textAlign: 'center',
                color: '#666',
                py: 2
              }}
            >
              {emptyStates.students}
            </Typography>
          )}
        </Box>

        {/* Parents Section */}
        <Box>
          <Typography
            variant="h6"
            sx={{
              color: '#F5A623',
              mb: 2,
              fontWeight: 500,
              px: 1
            }}
          >
            {sectionTitles.parents}
          </Typography>
          {groupedUsers.parent.map(user => (
            <UserCard key={user.id} user={user} />
          ))}
          {groupedUsers.parent.length === 0 && (
            <Typography
              sx={{
                textAlign: 'center',
                color: '#666',
                py: 2
              }}
            >
              {emptyStates.parents}
            </Typography>
          )}
        </Box>
      </motion.div>
    );
  };

  const renderAssignmentsTab = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {assignments.map(assignment => (
        <Paper
          key={assignment.id}
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: '#fff',
            borderRadius: 2,
            border: '1px solid #e0e0e0',
            '&:active': {
              transform: 'scale(0.98)',
              transition: 'transform 0.1s'
            }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ color: '#1a1a1a', fontWeight: 500 }}>
              {assignment.title}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              {assignment.description}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: 'rgba(245,166,35,0.1)',
                  borderRadius: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <TimerIcon sx={{ fontSize: 16, color: '#F5A623' }} />
                <Typography variant="caption" sx={{ color: '#F5A623' }}>
                  Due: {assignment.dueDate ? new Date(assignment.dueDate).toLocaleDateString() : 'No due date'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <IconButton
                onClick={() => handleEditOpen('assignment', assignment)}
                sx={{
                  color: '#F5A623',
                  backgroundColor: 'rgba(245,166,35,0.1)',
                  '&:active': { transform: 'scale(0.95)' }
                }}
              >
                  <EditIcon fontSize="small" />
                </IconButton>
              <IconButton
                onClick={() => handleDelete('assignment', assignment.id)}
                sx={{
                  color: '#ef4444',
                  backgroundColor: 'rgba(239,68,68,0.1)',
                  '&:active': { transform: 'scale(0.95)' }
                }}
              >
                  <DeleteIcon fontSize="small" />
                </IconButton>
            </Box>
          </Box>
        </Paper>
      ))}
      {assignments.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            backgroundColor: '#fff',
            borderRadius: 2,
            border: '1px dashed #e0e0e0'
          }}
        >
          <Typography variant="h6" sx={{ color: '#1a1a1a', mb: 1 }}>
            {emptyStates.assignments}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            There are no assignments at the moment.
          </Typography>
        </Box>
      )}
    </motion.div>
  );

  const renderLiveClassesTab = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {liveClasses.filter(liveClass => liveClass.status === 'active').map((liveClass) => (
        <Paper
          key={liveClass.id}
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: '#fff',
            borderRadius: 2,
            border: '1px solid #e0e0e0',
            '&:active': {
              transform: 'scale(0.98)',
              transition: 'transform 0.1s'
            }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#4CAF50',
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': {
                      transform: 'scale(0.95)',
                      boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.7)',
                    },
                    '70%': {
                      transform: 'scale(1)',
                      boxShadow: '0 0 0 10px rgba(76, 175, 80, 0)',
                    },
                    '100%': {
                      transform: 'scale(0.95)',
                      boxShadow: '0 0 0 0 rgba(76, 175, 80, 0)',
                    },
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: '#4CAF50',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  letterSpacing: '0.5px'
                }}
              >
                Live Now
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ color: '#1a1a1a', fontWeight: 500 }}>
              {liveClass.title}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              {liveClass.teacherName}
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', mb: 1 }}>
              {liveClass.subject}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                backgroundColor: 'rgba(0,0,0,0.04)',
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                alignSelf: 'flex-start',
                mb: 1
              }}
            >
              <TimerIcon sx={{ fontSize: 16, color: '#F5A623' }} />
              <Typography variant="caption" sx={{ color: '#1a1a1a' }}>
                Started: {new Date(liveClass.startTime).toLocaleString()}
              </Typography>
            </Box>
            {liveClass.description && (
              <Typography
                variant="body2"
                sx={{
                  color: '#666',
                  backgroundColor: 'rgba(0,0,0,0.03)',
                  p: 1.5,
                  borderRadius: 1,
                  mb: 1
                }}
              >
                {liveClass.description}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  const roomName = liveClass.roomName;
                  const streamingUrl = `/streaming/student.html?room=${roomName}`;
                  window.open(streamingUrl, '_blank');
                }}
                sx={{
                  backgroundColor: '#F5A623',
                  color: '#fff',
                  py: 1,
                  '&:hover': {
                    backgroundColor: '#e0951e'
                  }
                }}
              >
                Join Class
              </Button>
                  <Button
                fullWidth
                variant="outlined"
                onClick={() => handleDelete('liveClass', liveClass.id)}
                sx={{
                  borderColor: '#ef4444',
                  color: '#ef4444',
                  py: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(239,68,68,0.1)',
                    borderColor: '#ef4444'
                  }
                }}
              >
                End Class
                  </Button>
            </Box>
          </Box>
        </Paper>
      ))}
      {liveClasses.filter(liveClass => liveClass.status === 'active').length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            backgroundColor: '#fff',
            borderRadius: 2,
            border: '1px dashed #e0e0e0'
          }}
        >
          <Typography variant="h6" sx={{ color: '#1a1a1a', mb: 1 }}>
            {emptyStates.liveClasses}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            There are no active live classes at the moment.
          </Typography>
        </Box>
      )}
    </motion.div>
  );

  const renderActivityLogsTab = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
          {activityLogs.map(log => (
        <Paper
          key={log.id}
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: '#fff',
            borderRadius: 2,
            border: '1px solid #e0e0e0',
            '&:active': {
              transform: 'scale(0.98)',
              transition: 'transform 0.1s'
            }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ color: '#1a1a1a', fontWeight: 500 }}>
              {log.action}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              User: {log.userId}
            </Typography>
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                backgroundColor: 'rgba(245,166,35,0.1)',
                borderRadius: 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                alignSelf: 'flex-start'
              }}
            >
              <TimerIcon sx={{ fontSize: 16, color: '#F5A623' }} />
              <Typography variant="caption" sx={{ color: '#F5A623' }}>
                {log.timestamp ? new Date(log.timestamp.seconds * 1000).toLocaleString() : '-'}
              </Typography>
            </Box>
          </Box>
        </Paper>
      ))}
      {activityLogs.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            backgroundColor: '#fff',
            borderRadius: 2,
            border: '1px dashed #e0e0e0'
          }}
        >
          <Typography variant="h6" sx={{ color: '#1a1a1a', mb: 1 }}>
            {emptyStates.activity}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            There are no activity logs at the moment.
          </Typography>
        </Box>
      )}
    </motion.div>
  );

  const renderTabContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress sx={{ color: '#F5A623' }} />
        </Box>
      );
    }

    switch (tabIndex) {
      case 0: return renderUsersTab();
      case 1: return renderAssignmentsTab();
      case 2: return renderLiveClassesTab();
      case 3: return renderActivityLogsTab();
      default: return null;
    }
  };

  const renderBottomNav = () => (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        backgroundColor: '#fff',
        borderTop: '1px solid #e0e0e0',
        borderRadius: 0,
        boxShadow: 'none'
      }}
      elevation={0}
    >
      <BottomNavigation
        value={tabIndex}
        onChange={(e, newValue) => setTabIndex(newValue)}
        sx={{
          backgroundColor: 'transparent',
          height: 70,
          '& .MuiBottomNavigationAction-root': {
            color: '#999',
            '&.Mui-selected': {
              color: '#F5A623'
            },
            minWidth: 'auto',
            padding: '6px 0'
          }
        }}
      >
        <BottomNavigationAction label="Users" icon={<PeopleIcon />} />
        <BottomNavigationAction label="Assignments" icon={<AssignmentIcon />} />
        <BottomNavigationAction label="Live" icon={<LiveTvIcon />} />
        <BottomNavigationAction label="Activities" icon={<HistoryIcon />} />
      </BottomNavigation>
    </Paper>
  );

  return (
    <>
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        sx={{
          minHeight: '100vh',
          background: '#FAFAFA',
          pb: { xs: 9, sm: 9 },
          position: 'relative',
          fontFamily: 'inherit'
        }}
      >
        {/* App Bar */}
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#fff',
            borderBottom: '1px solid #e0e0e0',
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            px: 2,
            py: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <IconButton
              onClick={() => setDrawerOpen(true)}
              sx={{ color: '#1a1a1a' }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ color: '#1a1a1a', fontWeight: 600 }}>
              {tabIndex === 0 ? 'Users' :
               tabIndex === 1 ? 'Assignments' :
               tabIndex === 2 ? 'Live Classes' : 'Activities'}
            </Typography>
            {tabIndex === 0 && (
              <IconButton
                onClick={() => setAddUserDialogOpen(true)}
                sx={{
                  backgroundColor: '#F5A623',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#e0951e' },
                  width: 40,
                  height: 40
                }}
              >
                <AddIcon />
              </IconButton>
            )}
          </Box>
        </Paper>

        {/* Side Drawer */}
        <SwipeableDrawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onOpen={() => setDrawerOpen(true)}
          PaperProps={{
            sx: {
              backgroundColor: '#fff',
              width: '80%',
              maxWidth: 300
            }
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: '#1a1a1a' }}>Menu</Typography>
              <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: '#1a1a1a' }}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider sx={{ backgroundColor: '#e0e0e0' }} />
            <List>
              {[
                { text: 'Users', icon: <PeopleIcon /> },
                { text: 'Assignments', icon: <AssignmentIcon /> },
                { text: 'Live Classes', icon: <LiveTvIcon /> },
                { text: 'Activities', icon: <HistoryIcon /> }
              ].map((item, index) => (
                <ListItem
                  button
                  key={item.text}
                  onClick={() => {
                    setTabIndex(index);
                    setDrawerOpen(false);
                  }}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(245,166,35,0.1)'
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: tabIndex === index ? '#F5A623' : '#666' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{
                      '& .MuiListItemText-primary': {
                        color: tabIndex === index ? '#F5A623' : '#1a1a1a'
                      }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </SwipeableDrawer>

        {/* Main Content */}
        <Container
          maxWidth="xl"
          sx={{
            py: 2,
            px: { xs: 1, sm: 2 }
          }}
        >
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                backgroundColor: 'rgba(239,68,68,0.1)',
                color: '#ef4444',
                border: '1px solid #ef4444'
              }}
            >
              {error}
            </Alert>
          )}
          {renderTabContent()}
        </Container>

        {renderBottomNav()}

        {/* Edit Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              backgroundColor: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: 3
            }
          }}
        >
          <DialogTitle sx={{ color: '#1a1a1a', fontSize: '1.5rem', fontWeight: 600 }}>
            Edit {editType === 'user' ? 'User' : 'Assignment'}
          </DialogTitle>
          <DialogContent>
            {Object.keys(editData).map(key => {
              if (['id', 'createdAt', 'updatedAt'].includes(key)) return null;
              return (
                <TextField
                  key={key}
                  label={key}
                  value={editData[key]}
                  onChange={(e) => setEditData({ ...editData, [key]: e.target.value })}
                  fullWidth
                  margin="dense"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#e0e0e0',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(245,166,35,0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#F5A623',
                      },
                    },
                  }}
                  InputLabelProps={{
                    style: { color: '#666' }
                  }}
                  InputProps={{
                    style: { color: '#1a1a1a' }
                  }}
                />
              );
            })}
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={() => setEditDialogOpen(false)}
              sx={{
                color: '#666',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditSave}
              variant="contained"
              sx={{
                backgroundColor: '#F5A623',
                color: '#fff',
                '&:hover': { backgroundColor: '#e0951e' }
              }}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={addUserDialogOpen}
          onClose={() => setAddUserDialogOpen(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              backgroundColor: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: 3
            }
          }}
        >
          <DialogTitle sx={{ color: '#1a1a1a', fontSize: '1.5rem', fontWeight: 600 }}>
            Add New User
          </DialogTitle>
          <DialogContent>
            <TextField
              label={fieldLabels.displayName}
              value={newUserData.displayName}
              onChange={(e) => setNewUserData({ ...newUserData, displayName: e.target.value })}
              fullWidth
              margin="dense"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(245,166,35,0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#F5A623',
                  },
                },
              }}
              InputLabelProps={{
                style: { color: '#666' }
              }}
              InputProps={{
                style: { color: '#1a1a1a' }
              }}
            />
            <TextField
              label={fieldLabels.email}
              type="email"
              value={newUserData.email}
              onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
              fullWidth
              margin="dense"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(245,166,35,0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#F5A623',
                  },
                },
              }}
              InputLabelProps={{
                style: { color: '#666' }
              }}
              InputProps={{
                style: { color: '#1a1a1a' }
              }}
            />
            <TextField
              label={fieldLabels.password}
              type="password"
              value={newUserData.password}
              onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
              fullWidth
              margin="dense"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(245,166,35,0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#F5A623',
                  },
                },
              }}
              InputLabelProps={{
                style: { color: '#666' }
              }}
              InputProps={{
                style: { color: '#1a1a1a' }
              }}
            />
            <TextField
              select
              label={fieldLabels.role}
              value={newUserData.role}
              onChange={(e) => setNewUserData({ ...newUserData, role: e.target.value })}
              fullWidth
              margin="dense"
              variant="outlined"
              SelectProps={{
                native: true,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(245,166,35,0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#F5A623',
                  },
                },
              }}
              InputLabelProps={{
                style: { color: '#666' }
              }}
              InputProps={{
                style: { color: '#1a1a1a' }
              }}
            >
              <option value="student">{fieldLabels.roles.student}</option>
              <option value="teacher">{fieldLabels.roles.teacher}</option>
              <option value="admin">{fieldLabels.roles.admin}</option>
              <option value="parent">{fieldLabels.roles.parent}</option>
            </TextField>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={() => setAddUserDialogOpen(false)}
              sx={{
                color: '#666',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddUserSave}
              variant="contained"
              sx={{
                backgroundColor: '#F5A623',
                color: '#fff',
                '&:hover': { backgroundColor: '#e0951e' }
              }}
            >
              Create User
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default AdminDashboard;
